import { ChakraProvider } from '@chakra-ui/react'
import { ThemeProvider } from '@emotion/react'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactConfetti from 'react-confetti'

import theme from '@/constants/theme'

import { trackEvent } from '@/client/services/analytics'

import BrowserOnly from '@/components/UI/BrowserOnly'
import chakraConfig from '@/vendor/chakra/chakraConfig'

import { AppContext, HasuraContext, UserContext } from '../internal/context'
import type {
  AppContextType,
  AppModalType,
  HasuraContextType,
  UserContextType,
} from '../internal/types'
import AppContextModalBlocked from './AppContextModalBlocked'
import AppContextModalPendingApproval from './AppContextModalPendingApproval'
import AppContextModalRejected from './AppContextModalRejected'
import type { ModalState } from './types'

const MODAL_TYPE_TO_CATEGORY: Record<AppModalType, string> = {
  'pending-approval': 'Pending Approval Modal',
  blocked: 'Account Suspended Modal',
  rejected: 'Account Rejected Modal',
}

export default function AppContextProvider({
  children,
  prepass,
  dryRun,
  userContext,
  hasuraContext,
  onSetStatusCode,
}: React.PropsWithChildren<{
  prepass: boolean
  dryRun: boolean
  userContext: UserContextType
  onSetStatusCode: AppContextType['setStatusCode']
  hasuraContext: HasuraContextType | null
}>): React.ReactElement {
  const [modalState, setModalState] = useState<ModalState | null>(null)
  const [confettiUid, setConfettiUid] = useState<number | null>(null)

  const searchParams = useSearchParams()
  const searchParamConfetti = searchParams?.get('celebrate') ?? null

  const hideModal = useCallback(() => {
    setModalState(null)
  }, [setModalState])
  const showAppModal = useCallback(
    ({
      type,
      trackName,
      actionName,
    }: {
      type: AppModalType
      trackName: string
      actionName: string
    }) => {
      trackEvent(
        {
          category: MODAL_TYPE_TO_CATEGORY[type],
          action: 'Modal Shown',
        },
        {
          sourceName: trackName,
        }
      )
      setModalState({ type, trackName, actionName })
    },
    []
  )

  const showConfetti = useCallback(() => {
    // Change confetti component to a new instance
    // each time we want to re-show it
    setConfettiUid(Math.random())
  }, [])
  useEffect(() => {
    if (searchParamConfetti != null) {
      showConfetti()
    }
  }, [searchParamConfetti, showConfetti])

  const contextValue = useMemo(
    () => ({
      dryRun,
      prepass,
      showAppModal,
      showConfetti,
      setStatusCode: onSetStatusCode,
    }),
    [dryRun, onSetStatusCode, prepass, showAppModal, showConfetti]
  )

  return (
    <AppContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <UserContext.Provider value={userContext}>
          <ChakraProvider theme={chakraConfig} resetCSS={false}>
            <HasuraContext.Provider value={hasuraContext}>
              {modalState?.type === 'pending-approval' ? (
                <AppContextModalPendingApproval hideModal={hideModal} modalState={modalState} />
              ) : null}
              {modalState?.type === 'blocked' ? (
                <AppContextModalBlocked hideModal={hideModal} modalState={modalState} />
              ) : null}
              {modalState?.type === 'rejected' ? (
                <AppContextModalRejected hideModal={hideModal} modalState={modalState} />
              ) : null}
              <BrowserOnly>
                {confettiUid == null
                  ? null
                  : [
                      <ReactConfetti
                        numberOfPieces={1000}
                        recycle={false}
                        style={{ position: 'fixed', zIndex: 1000 }}
                        key={confettiUid}
                      />,
                    ]}
              </BrowserOnly>
              {children}
            </HasuraContext.Provider>
          </ChakraProvider>
        </UserContext.Provider>
      </ThemeProvider>
    </AppContext.Provider>
  )
}
